import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LinkProps } from "@tanstack/react-router";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@organizacaox/lai-design-system";
import type { LucideIcon } from "@organizacaox/lai-design-system/icons";
import type { NavGroupDef, NavLink } from "./nav-config.js";
import { navTranslationKey } from "./nav-config.js";
import { platformSearchScore } from "./platform-search-model.js";

export type PlatformSearchProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  directItems: NavLink[];
  groups: NavGroupDef[];
  onSelect: (to: LinkProps["to"]) => void;
};

function translatedLabel(t: ReturnType<typeof useTranslation>["t"], label: string): string {
  const key = navTranslationKey(label);
  return key ? t(key, { defaultValue: label }) : label;
}

function DestinationItem({
  icon: Icon,
  groupLabel,
  label,
  to,
  onSelect,
}: {
  icon: LucideIcon;
  groupLabel: string;
  label: string;
  to: LinkProps["to"];
  onSelect: (to: LinkProps["to"]) => void;
}) {
  return (
    <CommandItem value={`${label} ${groupLabel}`} onSelect={() => onSelect(to)}>
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </CommandItem>
  );
}

export function PlatformSearch({
  open,
  onOpenChange,
  directItems,
  groups,
  onSelect,
}: PlatformSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setQuery("");
    onOpenChange(nextOpen);
  }

  function handleSelect(to: LinkProps["to"]) {
    setQuery("");
    onOpenChange(false);
    onSelect(to);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t("platformSearch.title")}
      description={t("platformSearch.description")}
    >
      <Command filter={platformSearchScore}>
        <CommandInput
          autoFocus
          value={query}
          onValueChange={setQuery}
          placeholder={t("platformSearch.placeholder")}
          aria-label={t("platformSearch.placeholder")}
        />
        <CommandList>
          <CommandEmpty>{t("platformSearch.empty")}</CommandEmpty>
          {directItems.length > 0 ? (
            <CommandGroup heading={t("platformSearch.shortcuts")}>
              {directItems.map((item) => (
                <DestinationItem
                  key={String(item.to)}
                  icon={item.icon}
                  groupLabel={t("platformSearch.shortcuts")}
                  label={translatedLabel(t, item.label)}
                  to={item.to}
                  onSelect={handleSelect}
                />
              ))}
            </CommandGroup>
          ) : null}
          {groups.map((group) => {
            const groupLabel = translatedLabel(t, group.label);
            return (
              <CommandGroup key={group.label} heading={groupLabel}>
                {group.items.map((item) => (
                  <DestinationItem
                    key={String(item.to)}
                    icon={group.icon}
                    groupLabel={groupLabel}
                    label={translatedLabel(t, item.label)}
                    to={item.to}
                    onSelect={handleSelect}
                  />
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
